<script setup>
// ScheduleCampaignDeliveries.vue — orchestrator
import { computed, ref, watch, onMounted, onActivated } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useRoute, useRouter } from 'vue-router';

import DeliveriesHeader from './components/DeliveriesHeader.vue';
import DeliveriesTable from './components/DeliveriesTable.vue';
import DataTableToolbar from './components/DataTableToolbar.vue';
import ServerPagination from './components/ServerPagination.vue';

const props = defineProps({
  schedulingId: {
    type: [String, Number],
    required: true,
  },
  backRouteName: {
    type: String,
    default: 'campaigns_whatsapp_official_schedule',
  },
  hideCostCard: {
    type: Boolean,
    default: false,
  },
  hideBillable: {
    type: Boolean,
    default: false,
  },
});
const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

// Pagination & filter state
const searchQuery = ref('');
const statusFilter = ref([]);
const serverPagination = ref({ pageIndex: 0, pageSize: 10 });
const isInitialLoad = ref(true);

// Reference to the table component so we can pass its table instance to the toolbar
const tableRef = ref(null);
const tableInstance = computed(() => tableRef.value?.table ?? null);

let searchTimeout = null;

const resetFilters = () => {
  searchQuery.value = '';
  statusFilter.value = [];
  serverPagination.value.pageIndex = 0;
  isInitialLoad.value = true;
};

watch(
  () => props.schedulingId,
  (newId, oldId) => {
    if (newId !== oldId) resetFilters();
  }
);

watch(
  () => route.query.campaignId,
  (newId, oldId) => {
    if (newId !== oldId) resetFilters();
  }
);

// Store selectors
const uiFlags = computed(
  () => store.getters['campaigns/getUIFlags'] || { isFetchingDeliveries: false }
);

const rawDeliveries = computed(
  () => store.getters['campaigns/getCampaignScheduleDeliveries'] || []
);

const deliveriesMeta = computed(
  () =>
    store.getters['campaigns/getDeliveriesMeta'] || {
      current_page: 1,
      per_page: 50,
      total_count: 0,
      total_pages: 1,
    }
);

const templatePricingCategory = computed(() => {
  const serverPricing = store.getters['campaigns/getTemplatePricingCategory'];
  if (serverPricing && Object.keys(serverPricing).length > 0)
    return serverPricing;
  return {
    marketing: 0.332,
    utility: 0.036,
    service: 0,
    authentication: 0.036,
  };
});

const deliveryStats = computed(() => {
  const stats = store.getters['campaigns/getDeliveriesStats'] || {};
  const total = stats.total || 0;
  // stats.sent/delivered/read agora são CUMULATIVOS (funil: sent ⊇
  // delivered ⊇ read) — o backend soma os estágios. Somar delivered+read
  // aqui contaria as lidas 2×.
  const successful = stats.delivered || 0;
  const successRate =
    total > 0 ? ((successful / total) * 100).toFixed(1) : '0.0';
  const readRate =
    (stats.delivered || 0) > 0
      ? ((stats.read / stats.delivered) * 100).toFixed(1)
      : '0.0';
  return {
    total,
    billable: stats.billable || 0,
    sent: stats.sent || 0,
    delivered: stats.delivered || 0,
    read: stats.read || 0,
    successful,
    failed: stats.failed || 0,
    successRate: parseFloat(successRate),
    readRate: parseFloat(readRate),
  };
});

// Whether to show the initial loading overlay (first load, no data yet)
const showInitialLoading = computed(
  () => uiFlags.value.isFetchingDeliveries && isInitialLoad.value
);

// Data loading
const loadDeliveries = async (page = 1, perPage = 10) => {
  if (uiFlags.value.isFetchingDeliveries) return;
  try {
    const campaignId = route.query.campaignId;
    await store.dispatch('campaigns/listCampaignScheduleDeliveries', {
      campaignId,
      schedulingId: props.schedulingId,
      page,
      perPage,
      search: searchQuery.value,
      status: statusFilter.value.length > 0 ? statusFilter.value : null,
    });
  } catch {
    useAlert(t('CAMPAIGN.WHATSAPP_OFFICIAL.DELIVERIES.LOAD.ERROR_MESSAGE'));
  } finally {
    isInitialLoad.value = false;
  }
};

// Event handlers
const handlePageChange = newPage => {
  serverPagination.value.pageIndex = newPage - 1;
  loadDeliveries(newPage, serverPagination.value.pageSize);
};

const handlePageSizeChange = newSize => {
  serverPagination.value.pageSize = newSize;
  serverPagination.value.pageIndex = 0;
  loadDeliveries(1, newSize);
};

const handleSearch = query => {
  searchQuery.value = query;
  if (searchTimeout) clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    serverPagination.value.pageIndex = 0;
    loadDeliveries(1, serverPagination.value.pageSize);
  }, 500);
};

const handleStatusFilter = statuses => {
  statusFilter.value = statuses;
  serverPagination.value.pageIndex = 0;
  loadDeliveries(1, serverPagination.value.pageSize);
};

const handleGoToConversation = row => {
  const { display_id, message_id } = row.original;
  router.push({
    name: 'inbox_conversation',
    params: { accountId: route.params.accountId, conversation_id: display_id },
    query: { messageId: message_id },
  });
};

const goBack = () => {
  router.push({
    name: props.backRouteName,
    params: { accountId: route.params.accountId },
  });
};

onMounted(() => {
  resetFilters();
  loadDeliveries(1, serverPagination.value.pageSize);
});

onActivated(() => {
  resetFilters();
  loadDeliveries(1, serverPagination.value.pageSize);
});
</script>

<template>
  <div class="flex flex-col h-full bg-n-background overflow-x-hidden">
    <!-- Header -->
    <DeliveriesHeader @go-back="goBack" />

    <!-- Main -->
    <main class="flex flex-col flex-1 min-h-0 py-4">
      <div class="flex flex-col flex-1 min-h-0 mx-auto px-4 w-full space-y-4">
        <!-- Initial loading state -->
        <template v-if="showInitialLoading">
          <DeliveriesTable
            :data="[]"
            is-loading
            :hide-billable="hideBillable"
          />
        </template>

        <!-- Content -->
        <template v-else>
          <!-- Toolbar -->
          <DataTableToolbar
            v-if="tableInstance"
            :key="`toolbar-${route.query.campaignId}-${schedulingId}`"
            :table="tableInstance"
            :delivery-stats="deliveryStats"
            :template-pricing-category="templatePricingCategory"
            :is-loading="uiFlags.isFetchingDeliveries"
            :hide-cost-card="hideCostCard"
            @search="handleSearch"
            @status-filter="handleStatusFilter"
          />

          <!-- Table — flex-1 so it fills remaining vertical space -->
          <DeliveriesTable
            ref="tableRef"
            class="flex-1 min-h-0"
            :data="rawDeliveries"
            :is-loading="uiFlags.isFetchingDeliveries"
            :hide-billable="hideBillable"
            @go-to-conversation="handleGoToConversation"
          />

          <!-- Pagination — sibling outside table scroll area -->
          <ServerPagination
            v-if="rawDeliveries.length > 0"
            :meta="deliveriesMeta"
            :stats="deliveryStats"
            :is-loading="uiFlags.isFetchingDeliveries"
            :hide-billable="hideBillable"
            @page-change="handlePageChange"
            @page-size-change="handlePageSizeChange"
          />
        </template>
      </div>
    </main>
  </div>
</template>
